/**
 * Speaking Editor — Content (title, description, Page) + Questions (record-audio) + Configuration
 */

import * as React from 'react';
import { Badge, cn, createEmptyDocument, tiptapJsonToPlainText, isTiptapDocument } from '@ai-content-hub/content-kit';
import {
    type EditTabDescriptor,
    type CompositeQuestionsSidebarRow,
    generateId,
    type ContentEditProps,
    CompositeEditShell,
    CompositeEditContentTab,
    CompositeEditQuestionsLayout,
    CompositeEditSortableQuestionsLayout,
    orderItemsByQuestionsSidebarRows,
    persistQuestionsSidebarRows,
    reconcileQuestionsSidebarRows,
    resolvedQuestionsSidebarRows,
    useLocale,
} from '@ai-content-hub/content-core';
import {
    PageEdit,
    defaultPageData,
    RecordAudioEdit,
    recordAudioElementSchema,
    getCompositeQuestionType,
    type RecordAudioElementData,
    type PageElementData,
} from '@ai-content-hub/content-elements';
import type { BlockContentType } from '@ai-content-hub/content-elements';
import {
    defaultSpeakingData,
    type SpeakingData,
    type SpeakingItem,
    type SpeakingConfig,
} from './schema';
import { SpeakingGradingTypeField } from './SpeakingGradingTypeField';
import { FileText, HelpCircle, Settings } from 'lucide-react';
import map from 'lodash/map';

const PAGE_BLOCK_TYPES: BlockContentType[] = [
    'rich-text',
    'image',
    'audio',
    'video',
    'text',
    'office-document',
];

/**
 * When `true`, Questions tab shows the sidebar (reorder, add/remove, multiple prompts).
 * When `false`, only one prompt is edited (full-width card); legacy payloads with multiple
 * items keep extra prompts in data but only the first is editable here.
 */
const SPEAKING_MULTI_PROMPT_EDITOR = false;

const SPEAKING_QUESTION_TYPE_ID = 'record-audio';

function createItem(): SpeakingItem {
    return {
        id: generateId(),
        type: 'record-audio',
        data: { ...recordAudioElementSchema.defaultData } as RecordAudioElementData,
    };
}

function recordAudioLabel(): string {
    return getCompositeQuestionType(SPEAKING_QUESTION_TYPE_ID)?.label ?? 'Record audio';
}

function getItemPreview(item: SpeakingItem): string {
    const title = item.data.title;
    if (title && typeof title === 'object' && isTiptapDocument(title)) {
        const t = tiptapJsonToPlainText(title).trim();
        return t || 'Speaking prompt';
    }
    const t = typeof title === 'string' ? title.trim() : '';
    return t || 'Speaking prompt';
}

export type SpeakingEditProps = ContentEditProps<SpeakingData> & {
    config?: SpeakingConfig;
    className?: string;
};

export const SpeakingEdit: React.FC<SpeakingEditProps> = ({
    value: data,
    onChange,
    disabled = false,
    className,
    config,
    onConfigChange,
    configSlot,
    errors,
    language,
}) => {
    const { t, language: activeLanguage } = useLocale({
        config: config?.i18n,
        initialLanguage: language,
    });
    const [activeTab, setActiveTab] = React.useState<'content' | 'questions' | 'config'>('content');
    const singlePromptSeedRef = React.useRef(false);

    const safeData: SpeakingData = {
        ...defaultSpeakingData,
        ...data,
        pageContent: {
            ...defaultPageData,
            ...(data?.pageContent ?? {}),
            sections: data?.pageContent?.sections ?? defaultPageData.sections,
        },
        items: data?.items ?? [],
    };

    const items = safeData.items;
    const contentSectionCount = safeData.pageContent.sections?.length ?? 0;

    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [selectedSectionId, setSelectedSectionId] = React.useState<string | null>(null);

    // EAA-2083: only the exercise selection follows `items`; the section selection is left
    // alone. Every sidebar-rows change (typing a section title included) rebuilds `items`
    // as a new array, so with no exercises yet this effect cleared the selected section on
    // the first keystroke and closed its panel. A section that is really removed is
    // deselected by CompositeEditSortableQuestionsLayout itself.
    React.useEffect(() => {
        if (!SPEAKING_MULTI_PROMPT_EDITOR) {
            return;
        }
        if (items.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((prev) => {
            if (prev && items.some((i) => i.id === prev)) return prev;
            return items[0].id;
        });
    }, [items]);

    const updateData = (updates: Partial<SpeakingData>) => {
        onChange({
            ...safeData,
            ...updates,
        });
    };

    React.useEffect(() => {
        if (SPEAKING_MULTI_PROMPT_EDITOR || disabled) {
            return;
        }
        if (items.length > 0) {
            singlePromptSeedRef.current = false;
            return;
        }
        if (singlePromptSeedRef.current) {
            return;
        }
        singlePromptSeedRef.current = true;
        onChange({
            ...defaultSpeakingData,
            ...data,
            pageContent: {
                ...defaultPageData,
                ...(data?.pageContent ?? {}),
                sections: data?.pageContent?.sections ?? defaultPageData.sections,
            },
            items: [createItem()],
        });
        // Reset ref on cleanup so React StrictMode re-invocation can seed properly
        return () => {
            singlePromptSeedRef.current = false;
        };
    }, [items.length, disabled, data, onChange]);

    const handlePageContentChange = (pageContent: PageElementData) => {
        updateData({ pageContent });
    };

    // EAA-1857: grading type is activity-level, so it writes to config.scoring rather
    // than to a prompt. Without this the value could only ever be set by an AI job or
    // the Moodle importer, leaving an author no way to correct an imported prompt.
    const handleGradingQuestionTypeChange = (gradingQuestionType: string) => {
        if (disabled || !onConfigChange) return;
        onConfigChange({
            ...(config ?? {}),
            scoring: { ...(config?.scoring ?? {}), gradingQuestionType },
        } as SpeakingConfig);
    };


    const addItem = () => {
        if (disabled || !SPEAKING_MULTI_PROMPT_EDITOR) return;
        const newItem = createItem();
        const nextItems = [...items, newItem];
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextItems, (i) => i.id))
            ),
        });
        setSelectedId(newItem.id);
        setSelectedSectionId(null);
        setActiveTab('questions');
    };

    const removeItem = (itemId: string) => {
        if (disabled) return;
        const nextItems = items.filter((i) => i.id !== itemId);
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextItems, (i) => i.id))
            ),
        });
    };

    const updateItem = (itemId: string, next: SpeakingItem) => {
        if (disabled) return;
        updateData({
            items: map(items, (it) => (it.id === itemId ? next : it)),
        });
    };

    const handleQuestionsSidebarRowsChange = (rows: CompositeQuestionsSidebarRow[]) => {
        if (disabled || !SPEAKING_MULTI_PROMPT_EDITOR) return;
        const nextItems = orderItemsByQuestionsSidebarRows(items, rows);
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(rows),
        });
    };

    const handleAddSidebarSection = () => {
        if (disabled || !SPEAKING_MULTI_PROMPT_EDITOR) return;
        const sid = generateId();
        const base = resolvedQuestionsSidebarRows(
            safeData.questionsSidebarRows,
            map(items, (i) => i.id)
        );
        handleQuestionsSidebarRowsChange([
            {
                kind: 'section',
                section: { id: sid, title: '', description: createEmptyDocument() },
            },
            ...base,
        ]);
        setSelectedSectionId(sid);
        setSelectedId(null);
    };

    const sidebarRows = React.useMemo(
        () => resolvedQuestionsSidebarRows(safeData.questionsSidebarRows, map(items, (i) => i.id)),
        [items, safeData.questionsSidebarRows]
    );

    const selectedItem = SPEAKING_MULTI_PROMPT_EDITOR
        ? (items.find((i) => i.id === selectedId) ?? null)
        : (items[0] ?? null);

    const tabs: EditTabDescriptor[] = [
        { id: 'content', label: t('contentTab'), icon: FileText, badge: contentSectionCount },
        {
            id: 'questions',
            label: t('questionsTab'),
            icon: HelpCircle,
            badge: SPEAKING_MULTI_PROMPT_EDITOR ? items.length : Math.min(1, items.length),
        },
        ...(configSlot ? [{ id: 'config', label: t('configurationTab'), icon: Settings }] : []),
    ];

    const speakingAddOptions = [{ id: SPEAKING_QUESTION_TYPE_ID, label: recordAudioLabel() }];

    const renderRecordAudioDetail = (item: SpeakingItem): React.ReactNode => (
        <div className="speaking-edit-detail-inner space-y-4">
            <div className="speaking-edit-detail-heading flex items-center gap-2">
                <h3 className="text-lg font-semibold text-foreground">
                    Prompt {items.findIndex((i) => i.id === item.id) + 1}
                </h3>
                <Badge variant="outline" className="border-success/30 bg-success/10">
                    {recordAudioLabel()}
                </Badge>
            </div>
            <RecordAudioEdit
                value={item.data}
                onChange={(d) => updateItem(item.id, { ...item, data: d })}
                showAutoGrading={false}
                disabled={disabled}
                className="border-0 rounded-none space-y-0 p-0"
            />
        </div>
    );

    return (
        <CompositeEditShell
            prefix="speaking-edit"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as 'content' | 'questions' | 'config')}
            configSlot={configSlot}
            className={cn('speaking-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'content') {
                    return (
                        <CompositeEditContentTab
                            prefix="speaking-edit"
                            disabled={disabled}
                            title={{
                                id: 'speaking-title',
                                label: t('title'),
                                value: safeData.title ?? '',
                                onChange: (v) => updateData({ title: v }),
                                placeholder: t('enterTitle'),
                                required: true,
                            }}
                            titleInputClassName="speaking-edit-title-input"
                            description={{
                                id: 'speaking-description',
                                label: t('descriptionOptional'),
                                value: safeData.description ?? '',
                                onChange: (v) => updateData({ description: v }),
                                placeholder: t('enterDescription'),
                                rows: 3,
                            }}
                            descriptionInputClassName="speaking-edit-description-input"
                            page={
                                <PageEdit
                                    value={safeData.pageContent}
                                    onChange={handlePageContentChange}
                                    disabled={disabled}
                                    hideSettings
                                    allowedBlockTypes={PAGE_BLOCK_TYPES}
                                    className="speaking-edit-page-inner px-10! py-0!"
                                    config={config}
                                    language={activeLanguage}
                                />
                            }
                        />
                    );
                }
                if (tabId === 'questions') {
                    if (SPEAKING_MULTI_PROMPT_EDITOR) {
                        return (
                            <CompositeEditSortableQuestionsLayout
                                prefix="speaking-edit"
                                listTitle={t('prompts')}
                                emptyMessage={t('noPromptsYet')}
                                items={items}
                                selectedId={selectedId}
                                onSelectId={setSelectedId}
                                selectedSectionId={selectedSectionId}
                                onSelectSectionId={setSelectedSectionId}
                                disabled={disabled}
                                minItemsToKeep={0}
                                sidebarRows={sidebarRows}
                                onSidebarRowsChange={handleQuestionsSidebarRowsChange}
                                onAddSidebarSection={handleAddSidebarSection}
                                addSidebarSectionLabel={t('addSection')}
                                removeSidebarSectionLabel={t('removeSection')}
                                onRemoveItem={removeItem}
                                getItemLabel={(itemId, index) => {
                                    const item = items.find((i) => i.id === itemId);
                                    if (!item) return '';
                                    return `${index + 1}. ${getItemPreview(item)}`;
                                }}
                                addButtonLabel={t('addPrompt')}
                                addButtonOptions={speakingAddOptions}
                                addButtonClassName="speaking-edit-add-prompt"
                                onAddItem={() => addItem()}
                                sectionDetailTitle={t('section')}
                                detailTitle={t('promptDetail')}
                                detailBadge={
                                    <Badge
                                        variant="outline"
                                        className="border-success/30 bg-success/10"
                                    >
                                        {recordAudioLabel()}
                                    </Badge>
                                }
                                renderItemDetail={({ itemId }) => {
                                    const item = items.find((i) => i.id === itemId);
                                    if (!item) {
                                        return null;
                                    }
                                    return (
                                        <RecordAudioEdit
                                            value={item.data}
                                            onChange={(d) =>
                                                updateItem(item.id, {
                                                    ...item,
                                                    data: d,
                                                })
                                            }
                                            disabled={disabled}
                                            className="border-0 rounded-none space-y-0 p-0"
                                        />
                                    );
                                }}
                                detailContent={null}
                                detailEmptyMessage={t('selectPromptMessage')}
                                removeItemLabel={t('removePrompt')}
                                richTextTranslate={t}
                            />
                        );
                    }
                    return (
                        <CompositeEditQuestionsLayout
                            variant="detail-only"
                            prefix="speaking-edit"
                            detail={
                                <>
                                    {items.length > 1 ? (
                                        <p className="speaking-edit-multi-legacy-notice mb-4 text-xs text-muted-foreground rounded-sm border border-border bg-muted/30 px-3 py-2">
                                            {t('multiPromptLegacyNotice')}
                                        </p>
                                    ) : null}
                                    {selectedItem ? (
                                        renderRecordAudioDetail(selectedItem)
                                    ) : (
                                        <p className="text-sm text-muted-foreground text-center py-12">
                                            {t('preparingPrompt')}
                                        </p>
                                    )}
                                    {/* EAA-1890: the grading type sits directly under the
                                        record-audio block, whose last control is the
                                        "Tự động chấm điểm" checkbox that gates it. It used
                                        to head the panel, so ticking a checkbox at the
                                        bottom made a field appear at the top, off screen.
                                        No rule between them on purpose — a divider would
                                        separate a control from the field it gates. */}
                                    <div className="speaking-edit-detail-grading mt-4">
                                        <SpeakingGradingTypeField
                                            value={config?.scoring?.gradingQuestionType}
                                            onChange={handleGradingQuestionTypeChange}
                                            disabled={disabled || !onConfigChange}
                                            t={t}
                                        />
                                    </div>
                                </>
                            }
                        />
                    );
                }
                return null;
            }}
        />
    );
};

export default SpeakingEdit;
